import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type EnhancePageContent = {
  id: string;
  hero_image_url: string | null;
  hero_eyebrow: string | null;
  hero_title: string | null;
  hero_subtitle: string | null;
};

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

const COLS = "id,hero_image_url,hero_eyebrow,hero_title,hero_subtitle";

export const getEnhancePageContent = createServerFn({ method: "GET" }).handler(
  async (): Promise<EnhancePageContent | null> => {
    const sb = publicClient();
    const { data, error } = await sb
      .from("enhance_page_content")
      .select(COLS)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (data as EnhancePageContent | null) ?? null;
  },
);

const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  hero_image_url: z.string().nullable(),
  hero_eyebrow: z.string().nullable(),
  hero_title: z.string().nullable(),
  hero_subtitle: z.string().nullable(),
});

export const upsertEnhancePageContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => upsertSchema.parse(d))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const payload = {
      hero_image_url: data.hero_image_url,
      hero_eyebrow: data.hero_eyebrow,
      hero_title: data.hero_title,
      hero_subtitle: data.hero_subtitle,
    };
    if (data.id) {
      const { error } = await sb
        .from("enhance_page_content")
        .update(payload)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await sb
      .from("enhance_page_content")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });
